import * as React from "react"

import {
  ConfigPropertiesPanel,
  type ConfigProperty,
  type ConfigPropertyValue,
} from "@/components/alfred/config-properties-panel"
import {
  getAlfredMaxLengthError,
  MAX_ALFRED_INDEXED_STRING_LENGTH,
} from "@/lib/alfred-field-validation"
import type {
  AlfredClientConfig,
  CreateAlfredClientRequest,
  UpdateAlfredClientRequest,
} from "@/types/alfred-client"

type AlfredClientEditorProps =
  | {
      mode: "create"
      client?: never
      onSubmit: (data: CreateAlfredClientRequest) => Promise<void>
      isSubmitting?: boolean
      onDirtyChange?: (isDirty: boolean) => void
      resetSignal?: number
    }
  | {
      mode: "edit"
      client: AlfredClientConfig
      onSubmit: (data: UpdateAlfredClientRequest) => Promise<void>
      isSubmitting?: boolean
      onDirtyChange?: (isDirty: boolean) => void
      resetSignal?: number
    }

function required(label: string, value: ConfigPropertyValue): string | undefined {
  return typeof value === "string" && value.trim() ? undefined : `${label} is required`
}

export function AlfredClientConfigEditor(props: AlfredClientEditorProps) {
  const { mode, isSubmitting = false, onDirtyChange, resetSignal = 0 } = props
  const properties = React.useMemo<ConfigProperty[]>(() => {
    const clientId = mode === "edit" ? props.client.clientId : ""
    const venueCode = mode === "edit" ? props.client.venueCode : ""
    return [
      mode === "edit"
        ? {
            key: "clientId",
            label: "Client ID",
            section: "Client Information",
            type: "readonly",
            value: clientId,
          }
        : {
            key: "clientId",
            label: "Client ID",
            section: "Client Information",
            type: "string",
            value: clientId,
            required: true,
            placeholder: "e.g., dodgers",
            characterLimit: MAX_ALFRED_INDEXED_STRING_LENGTH,
            autoFocus: true,
            validate: (value: ConfigPropertyValue) => {
              const requiredError = required("Client ID", value)
              if (requiredError) return requiredError
              const text = value as string
              return getAlfredMaxLengthError("Client ID", text) ??
                (/^[A-Za-z0-9-]+$/.test(text)
                  ? undefined
                  : "Client ID can only contain letters, numbers, and hyphens")
            },
          },
      {
        key: "venueCode",
        label: "Venue Code",
        section: "Client Information",
        type: "string",
        value: venueCode,
        required: true,
        placeholder: "e.g., LAD",
        autoFocus: mode === "edit",
        validate: (value) => required("Venue Code", value),
      },
    ]
  }, [mode, props])

  const handleSubmit = async (values: Record<string, ConfigPropertyValue | null>) => {
    const venueCode = String(values.venueCode ?? "")
    if (mode === "edit") {
      await props.onSubmit({ venueCode })
      return
    }
    await props.onSubmit({
      clientId: String(values.clientId ?? ""),
      venueCode,
    })
  }

  return (
    <ConfigPropertiesPanel
      properties={properties}
      resetKey={`${mode === "edit" ? `${props.client.id}:${props.client.venueCode}` : "new-client"}:${resetSignal}`}
      onSave={handleSubmit}
      isSaving={isSubmitting}
      defaultEditing
      editingLocked
      showUseDefault={false}
      configurationTitle={null}
      saveConfirmationTitle={mode === "edit" ? "Save client changes?" : "Create this client?"}
      saveConfirmationDescription={
        mode === "edit"
          ? "Are you sure you want to save these changes to this client?"
          : "The client will be created with this identifier and venue code."
      }
      submitLabel={mode === "edit" ? "Save Changes" : "Create Client"}
      savingLabel={mode === "edit" ? "Saving..." : "Creating..."}
      confirmationActionLabel={mode === "edit" ? "Save Changes" : "Create Client"}
      confirmSave={false}
      onDirtyChange={onDirtyChange}
    />
  )
}
